/**
 * MD-25 SEO Dashboard hook - F-A-40.
 *
 * Reuse pattern useAdminAgentsApi: fetch JSON tu /api/v1/admin/seo/* voi
 * JWT Bearer header tu localStorage, normalize error body.
 *
 * Spec: specs/domains/admin/F-A-SEO-DASHBOARD.md §6.
 */

import { useEffect, useState, useCallback } from 'react';

const API_BASE = '/api/v1/admin/seo';

export interface UseAdminSeoState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

function extractErrorMessage(body: unknown, status: number): string {
  if (!body || typeof body !== 'object') return `HTTP ${status}`;
  const obj = body as Record<string, unknown>;
  const err = obj.error;
  if (typeof err === 'string' && err.trim()) return err;
  if (err && typeof err === 'object') {
    const inner = err as Record<string, unknown>;
    if (typeof inner.message === 'string' && inner.message.trim()) return inner.message;
    if (typeof inner.code === 'string') return inner.code;
  }
  if (typeof obj.message === 'string' && obj.message.trim()) return obj.message;
  return `HTTP ${status}`;
}

function toErrorString(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  try {
    return JSON.stringify(e);
  } catch {
    return 'Loi khong xac dinh';
  }
}

function getAuthHeader(): Record<string, string> {
  try {
    const token = localStorage.getItem('asklaw_access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export function useAdminSeoApi<T>(path: string): UseAdminSeoState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}${path}`, { headers: getAuthHeader() });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(extractErrorMessage(body, res.status));
      }
      const json = (await res.json()) as T;
      setData(json);
    } catch (e) {
      setError(toErrorString(e));
    } finally {
      setLoading(false);
    }
  }, [path]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

// ---------- Public artifact hook (FE fetch /seo-audit.json static) ----------

export function useSeoAuditArtifact<T>(url = '/seo-audit.json'): UseAdminSeoState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as T;
      setData(json);
    } catch (e) {
      setError(toErrorString(e));
    } finally {
      setLoading(false);
    }
  }, [url]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}
